import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { migrateLocalDataToFirestore, hasLocalCacheToMigrate } from '../utils/localDataMigration';

const AuthContext = createContext();

/** Força refresh do ID token para pegar Custom Claims novas. */
async function refreshAuthClaims(user) {
  if (!user) return;
  try {
    await user.getIdToken(true);
  } catch (e) {
    console.warn('Falha ao renovar token/claims:', e?.message || e);
  }
}

let pendingMigrationPromise = null;

const runLocalDataMigration = async (userId) => {
  if (!hasLocalCacheToMigrate()) return false;

  if (!pendingMigrationPromise) {
    pendingMigrationPromise = migrateLocalDataToFirestore(userId).finally(() => {
      pendingMigrationPromise = null;
    });
  }

  await pendingMigrationPromise;
  return true;
};

/** Dispara migração sem bloquear o fluxo de login/redirect. */
const scheduleLocalDataMigration = (userId, onDone) => {
  if (!hasLocalCacheToMigrate()) return;
  Promise.resolve()
    .then(() => runLocalDataMigration(userId))
    .then((migrated) => {
      if (migrated && typeof onDone === 'function') onDone();
    })
    .catch(() => {});
};

function buildUserState(currentUser, userData, tokenClaims = {}) {
  return {
    uid: currentUser.uid,
    email: currentUser.email,
    name: userData?.name || '',
    phone: userData?.phone || '',
    tag: userData?.tag || 'CLI',
    profileId: userData?.profileId || null,
    role: tokenClaims.role || userData?.role || 'client',
    isAdmin:
      tokenClaims.admin === true ||
      userData?.tag === 'ADM' ||
      userData?.tag === 'ADMIN' ||
      userData?.isAdmin === true
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localDataMigrationToken, setLocalDataMigrationToken] = useState(0);
  /** Evita getDoc(users/{uid}) duplicado logo após login()/signup()/Google. */
  const profileCacheRef = useRef({ uid: null, data: null, at: 0 });

  const bumpLocalDataMigrationToken = () => {
    setLocalDataMigrationToken((value) => value + 1);
  };

  const rememberProfile = (uid, data) => {
    profileCacheRef.current = { uid, data, at: Date.now() };
  };

  const takeFreshProfile = (uid, maxAgeMs = 8000) => {
    const cached = profileCacheRef.current;
    if (cached.uid === uid && Date.now() - cached.at < maxAgeMs) {
      return cached.data;
    }
    return null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        await refreshAuthClaims(currentUser);

        scheduleLocalDataMigration(currentUser.uid, bumpLocalDataMigrationToken);

        let userData = takeFreshProfile(currentUser.uid);
        if (userData == null) {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          userData = userDoc.data() || {};
          rememberProfile(currentUser.uid, userData);
        }

        let tokenClaims = {};
        try {
          const tokenResult = await currentUser.getIdTokenResult();
          tokenClaims = tokenResult.claims || {};
        } catch {
          tokenClaims = {};
        }

        setUser(buildUserState(currentUser, userData, tokenClaims));
      } else {
        profileCacheRef.current = { uid: null, data: null, at: 0 };
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      scheduleLocalDataMigration(result.user.uid, bumpLocalDataMigrationToken);

      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      const userData = userDoc.data() || {};
      rememberProfile(result.user.uid, userData);

      const userState = buildUserState(result.user, userData);
      setUser(userState);

      return userData?.tag === 'ADM' || userData?.tag === 'ADMIN';
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    }
  };

  const signup = async (email, password, name, phone) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      const userData = {
        email: result.user.email,
        name: name || '',
        phone: phone || '',
        tag: 'CLI',
        createdAt: new Date()
      };

      const db = getFirestore();
      await setDoc(doc(db, 'users', result.user.uid), userData, { merge: true });
      rememberProfile(result.user.uid, userData);

      scheduleLocalDataMigration(result.user.uid, bumpLocalDataMigrationToken);

      setUser(buildUserState(result.user, userData));

      return result.user;
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      throw error;
    }
  };

  const updateUserData = async (uid, userData) => {
    try {
      const db = getFirestore();
      await setDoc(doc(db, 'users', uid), userData, { merge: true });
      
      setUser(prev => ({
        ...prev,
        ...userData
      }));
      if (profileCacheRef.current.uid === uid) {
        rememberProfile(uid, { ...(profileCacheRef.current.data || {}), ...userData });
      }
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        return {
          isNewUser: true,
          user: result.user
        };
      } else {
        const userData = userDoc.data() || {};
        rememberProfile(result.user.uid, userData);
        const userState = buildUserState(result.user, {
          ...userData,
          name: userData?.name || result.user.displayName || ''
        });

        scheduleLocalDataMigration(result.user.uid, bumpLocalDataMigrationToken);
        setUser(userState);

        return {
          isNewUser: false,
          isAdmin: userData?.tag === 'ADM' || userData?.tag === 'ADMIN'
        };
      }
    } catch (error) {
      console.error('Erro no login com Google:', error);
      throw error;
    }
  };

  const completeGoogleSignup = async (uid, name, phone) => {
    try {
      const userData = {
        name: name || '',
        phone: phone || '',
        tag: 'CLI',
        createdAt: new Date()
      };
      const db = getFirestore();
      await setDoc(doc(db, 'users', uid), userData, { merge: true });
      rememberProfile(uid, { ...(profileCacheRef.current.data || {}), ...userData });

      scheduleLocalDataMigration(uid, bumpLocalDataMigrationToken);

      setUser(prev => ({
        ...prev,
        name: name || '',
        phone: phone || '',
        tag: 'CLI',
        isAdmin: false
      }));
    } catch (error) {
      console.error('Erro ao completar cadastro Google:', error);
      throw error;
    }
  };

  const checkEmailForPasswordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { exists: true, isGoogleAccount: false, emailSent: true };
    } catch (error) {
      console.error('Erro ao enviar email de redefinição:', error);
      if (error.code === 'auth/invalid-email') {
        throw new Error('Email inválido');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.');
      } else {
        throw new Error('Erro ao enviar email de redefinição. Verifique se o email está correto e tente novamente.');
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Erro no logout:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      signup,
      updateUserData,
      loginWithGoogle,
      completeGoogleSignup,
      checkEmailForPasswordReset,
      localDataMigrationToken
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
